import { memo, type RefObject, useCallback, useEffect, useMemo, useState } from 'react'
import { type NodeProps, useReactFlow, useStore } from '@xyflow/react'
import { HandleOverlay } from './handle-overlay.tsx'
import {
    DEFAULT_MAGNETIC_HANDLE_CONFIG,
    type MagneticHandleConfig,
    resolveDefaultMagneticHandlePoint
} from '../model/model'
import { useMagneticHandlesRuntime, useMagneticHandlesViewportMoving } from '../model/magnetic-handles-provider'
import { HandleTriggerZone, type InternalMagneticHandleTriggerZoneHoverState, } from './handle-trigger-zone.tsx'

type LocalHoverState = InternalMagneticHandleTriggerZoneHoverState

function resolveEffectiveNodeSize(
    nodeRef: RefObject<HTMLDivElement | null>,
    nodeInfo?: { height?: number; width?: number },
) {
    const element = nodeRef.current

    return {
        width: nodeInfo?.width || element?.clientWidth || 0,
        height: nodeInfo?.height || element?.clientHeight || 0,
    }
}

export const MagneticConnectionHandles = memo(function MagneticConnectionHandles({
    nodeRef,
    props,
    zoom,
    isHovered = false,
    showPlus = true,
    hideLeft = false,
    hideOverlay = false,

}: {
    props: NodeProps
    zoom: number,
    config?: Partial<MagneticHandleConfig>
    nodeRef: RefObject<HTMLDivElement | null>
    isHovered?: boolean
    showPlus?: boolean
    hideLeft?: boolean
    hideOverlay?: boolean
}) {
    const resolvedNodeId = props.id
    const limitVisible = zoom >= 0.3
    const { getViewport } = useReactFlow()
    const nodeSize = useStore((s) => {
        const node = s.nodeLookup.get(resolvedNodeId)
        return {
            width: node?.measured?.width ?? node?.width ?? 0,
            height: node?.measured?.height ?? node?.height ?? 0
        }
    }, (a, b) => a.width === b.width && a.height === b.height)
    
    const [localHoverState, setLocalHoverState] = useState<LocalHoverState | null>(null)
    const { connectingFromNodeId, runtime } = useMagneticHandlesRuntime()
    const viewportMoving = useMagneticHandlesViewportMoving()
    const { height: effectiveNodeHeight, width: effectiveNodeWidth } = resolveEffectiveNodeSize(nodeRef, nodeSize)
    const handleHoverStateChange = useCallback((state: LocalHoverState | null) => {
        setLocalHoverState(state)
    }, [])
    const resolveCurrentZoom = useCallback(() => getViewport().zoom, [getViewport])
    const leftDefaultPoint = useMemo(
        () => resolveDefaultMagneticHandlePoint(effectiveNodeWidth, effectiveNodeHeight, 'left', DEFAULT_MAGNETIC_HANDLE_CONFIG),
        [effectiveNodeHeight, effectiveNodeWidth],
    )
    const rightDefaultPoint = useMemo(
        () => resolveDefaultMagneticHandlePoint(effectiveNodeWidth, effectiveNodeHeight, 'right', DEFAULT_MAGNETIC_HANDLE_CONFIG),
        [effectiveNodeHeight, effectiveNodeWidth],
    )

    useEffect(() => {
        if (!resolvedNodeId) {
            return
        }

        return runtime.registerNode({
            config: DEFAULT_MAGNETIC_HANDLE_CONFIG,
            nodeId: resolvedNodeId,
            nodeRef,
        })
    }, [nodeRef, resolvedNodeId, runtime])

    return (
        <>
            <HandleTriggerZone
                limitVisible={limitVisible}
                defaultPoint={leftDefaultPoint}
                effectiveNodeHeight={effectiveNodeHeight}
                effectiveNodeWidth={effectiveNodeWidth}
                hidden={viewportMoving || hideLeft}
                nodeId={resolvedNodeId}
                nodeRef={nodeRef}
                onHoverStateChange={handleHoverStateChange}
                resolveCurrentZoom={resolveCurrentZoom}
                side="left"
            />
            <HandleTriggerZone
                limitVisible={limitVisible}
                defaultPoint={rightDefaultPoint}
                effectiveNodeHeight={effectiveNodeHeight}
                effectiveNodeWidth={effectiveNodeWidth}
                hidden={viewportMoving}
                nodeId={resolvedNodeId}
                nodeRef={nodeRef}
                onHoverStateChange={handleHoverStateChange}
                resolveCurrentZoom={resolveCurrentZoom}
                side="right"
            />
            {limitVisible && <HandleOverlay
                hoverState={localHoverState}
                isConnectingFromNode={connectingFromNodeId === resolvedNodeId}
                leftDefaultPoint={leftDefaultPoint}
                nodeId={resolvedNodeId}
                nodeRef={nodeRef}
                rightDefaultPoint={rightDefaultPoint}
                selected={props.selected || isHovered}
                showLeftPlus={showPlus && !hideLeft}
                showRightPlus={showPlus}
                forceHide={hideOverlay}
            />}
        </>
    )
})
